import React, { useState } from "react";
import { createPortal } from "react-dom";
import { X, KeyRound, User, Lock, Mail, ShieldCheck, Trash2, Eye, EyeOff, CheckCircle2, XCircle } from "lucide-react";
import api from "@/lib/api";
import { useT } from "@/lib/i18n";

const SYMBOLS_REGEX = /[!@#$%^&*()_+\-=[\]{};:,.<>?/|~]/;

function passwordChecks(pw) {
  return {
    length: pw.length >= 8,
    letter: /[A-Za-z]/.test(pw),
    digit: /\d/.test(pw),
    symbol: SYMBOLS_REGEX.test(pw),
  };
}

function strengthOk(pw) {
  const c = passwordChecks(pw);
  return c.length && c.letter && c.digit && c.symbol;
}

function Check({ ok, label }) {
  return (
    <div className={`flex items-center gap-1 text-[11px] ${ok ? "text-emerald-700" : "text-gray-400"}`}>
      {ok ? <CheckCircle2 size={11} /> : <XCircle size={11} />} {label}
    </div>
  );
}

export default function SupervisorLoginModal({ admin, onClose, onSaved }) {
  const { t } = useT();
  const [form, setForm] = useState({
    username: admin?.login_username || "",
    password: "",
    email: admin?.email || "",
  });
  const [showPw, setShowPw] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const checks = passwordChecks(form.password);
  const isUpdate = !!admin?.has_login;
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const canSubmit = form.username.length >= 3 && strengthOk(form.password) && !loading;

  const fmtDetail = (d, fallback) => {
    if (typeof d === "string") return d;
    if (Array.isArray(d))
      return d.map((x) => (x && typeof x.msg === "string" ? x.msg : "")).filter(Boolean).join(" — ") || fallback;
    return fallback;
  };

  const save = async () => {
    setError("");
    setLoading(true);
    try {
      await api.post(`/admins/${admin.id}/credentials`, {
        username: form.username.trim().toLowerCase(),
        password: form.password,
        email: form.email.trim().toLowerCase(),
      });
      setSuccess(t(isUpdate ? "supervisor_login_updated_ok" : "supervisor_login_created_ok"));
      setTimeout(() => {
        onSaved && onSaved();
        onClose();
      }, 1400);
    } catch (err) {
      const d = err.response?.data?.detail;
      setError(fmtDetail(d, t("supervisor_login_save_failed")));
    } finally {
      setLoading(false);
    }
  };

  const revoke = async () => {
    if (!window.confirm(t("confirm_revoke_supervisor_login"))) return;
    setLoading(true);
    try {
      await api.delete(`/admins/${admin.id}/credentials`);
      onSaved && onSaved();
      onClose();
    } catch (err) {
      const d = err.response?.data?.detail;
      setError(typeof d === "string" ? d : t("supervisor_login_save_failed"));
    } finally {
      setLoading(false);
    }
  };

  return createPortal(
    <div
      className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4 modal-backdrop animate-fade-in"
      onClick={onClose}
      data-testid="supervisor-login-modal"
    >
      <div
        className="bg-white w-full sm:max-w-md max-h-[95vh] sm:max-h-[90vh] rounded-t-2xl sm:rounded-2xl card-shadow overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="sticky top-0 bg-white border-b border-gray-100 px-5 py-3 flex items-center justify-between z-10">
          <div className="flex items-center gap-2 text-teal-900">
            <KeyRound size={18} />
            <h3 className="font-bold">{isUpdate ? t("supervisor_login_edit") : t("supervisor_login_create")}</h3>
          </div>
          <button onClick={onClose} className="bg-gray-100 hover:bg-gray-200 rounded-full p-2" data-testid="sup-login-close">
            <X size={16} />
          </button>
        </div>

        <div className="px-5 py-4 space-y-4">
          <div className="bg-teal-50 border border-teal-100 rounded-xl px-3 py-2 text-sm text-teal-900">
            <div className="flex items-center gap-2 font-semibold">
              <ShieldCheck size={14} /> {admin?.name}
            </div>
            <p className="text-[11px] text-teal-700 mt-0.5">{t("supervisor_login_hint")}</p>
          </div>

          <div>
            <label className="inp-label flex items-center gap-2"><User size={13} /> {t("username")}</label>
            <input
              type="text"
              value={form.username}
              onChange={set("username")}
              className="inp"
              style={{ direction: "ltr" }}
              placeholder="e.g. sulaiman_sup"
              data-testid="sup-login-username-input"
            />
            <p className="text-[10px] text-gray-400 mt-1">{t("username_hint")}</p>
          </div>

          <div>
            <label className="inp-label flex items-center gap-2"><Lock size={13} /> {t("password")}</label>
            <div className="relative">
              <input
                type={showPw ? "text" : "password"}
                value={form.password}
                onChange={set("password")}
                className="inp pe-10"
                style={{ direction: "ltr" }}
                placeholder={isUpdate ? t("password_update_placeholder") : "••••••••"}
                data-testid="sup-login-password-input"
              />
              <button type="button" onClick={() => setShowPw(!showPw)}
                className="absolute top-1/2 -translate-y-1/2 end-2 text-gray-400 p-1" tabIndex={-1}>
                {showPw ? <EyeOff size={14} /> : <Eye size={14} />}
              </button>
            </div>
            <div className="grid grid-cols-2 gap-1 mt-1.5">
              <Check ok={checks.length} label={t("pw_check_length")} />
              <Check ok={checks.letter} label={t("pw_check_letter")} />
              <Check ok={checks.digit} label={t("pw_check_digit")} />
              <Check ok={checks.symbol} label={t("pw_check_symbol")} />
            </div>
          </div>

          <div>
            <label className="inp-label flex items-center gap-2"><Mail size={13} /> {t("email")} <span className="text-gray-400 text-[10px]">({t("optional")})</span></label>
            <input
              type="email"
              value={form.email}
              onChange={set("email")}
              className="inp"
              style={{ direction: "ltr" }}
              placeholder="name@example.com"
              data-testid="sup-login-email-input"
            />
          </div>

          {success && (
            <div className="bg-emerald-50 text-emerald-700 rounded-lg px-3 py-2 w-full text-center text-sm font-semibold" data-testid="sup-login-success">{success}</div>
          )}
          {error && (
            <div className="status-expired pill px-3 py-2 w-full justify-center" data-testid="sup-login-error">{error}</div>
          )}

          <div className="flex items-center gap-2 pt-1">
            <button
              onClick={save}
              disabled={!canSubmit}
              className="btn btn-primary flex-1 justify-center"
              data-testid="sup-login-save"
            >
              {loading ? t("processing") : (isUpdate ? t("save") : t("create_login"))}
            </button>
            {isUpdate && (
              <button
                onClick={revoke}
                disabled={loading}
                className="btn btn-ghost text-rose-600 hover:bg-rose-50"
                data-testid="sup-login-revoke"
                title={t("revoke_login")}
              >
                <Trash2 size={14} />
              </button>
            )}
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}
